import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { createSepayQr, BillingCycle } from '@/lib/sepay';
import type { Plan } from '@/types';

export function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [qrData, setQrData] = useState<{ qr_url: string; reference: string; amount: number; expires_at: number } | null>(null);
  const [qrError, setQrError] = useState('');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await api.getPlans();
      setPlans(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải bảng giá');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQr = async (planId: string) => {
    if (!user) return;
    setQrError('');
    setSelectedPlan(planId);
    try {
      const plan = plans.find(p => p.id === planId);
      if (!plan) throw new Error('Plan not found');
      const cycle: BillingCycle = 'monthly';
      const data = await createSepayQr(planId, user.id, plan.priceMonthly, cycle);
      setQrData(data);
    } catch (err) {
      setQrError(err instanceof Error ? err.message : 'Tạo QR thất bại');
    }
  };

  const handleCloseQr = () => {
    setQrData(null);
    setSelectedPlan(null);
    setQrError('');
  };

  const formatPrice = (price: number, cycle: string) => {
    if (price === 0) return 'Miễn phí';
    const formatted = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
    return `${formatted}/${cycle === 'monthly' ? 'tháng' : 'năm'}`;
  };

  // All possible features for comparison table
  const allFeatures = [
    { key: 'ocr_basic', label: 'OCR cơ bản' },
    { key: 'ocr_advanced', label: 'OCR nâng cao (multi-model)' },
    { key: 'batch_processing', label: 'Xử lý hàng loạt' },
    { key: 'google_sheets', label: 'Tích hợp Google Sheets' },
    { key: 'history_search', label: 'Lịch sử & tìm kiếm' },
    { key: 'categories', label: 'Phân loại hóa đơn' },
    { key: 'api_access', label: 'API access' },
    { key: 'priority_support', label: 'Hỗ trợ ưu tiên' },
    { key: 'custom_fields', label: 'Trường tùy chỉnh' },
    { key: 'webhook', label: 'Webhook' },
    { key: 'sso', label: 'SSO' },
    { key: 'dedicated_support', label: 'Hỗ trợ chuyên biệt' },
  ];

  const hasFeature = (plan: Plan, featureKey: string) => plan.features.includes(featureKey);

  if (loading) {
    return (
      <div className="min-h-screen py-20">
        <div className="container">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand border-t-transparent" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 md:py-20 bg-bg">
      <div className="container">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-text mb-4 display">Bảng giá</h1>
          <p className="text-lg text-text-muted max-w-2xl mx-auto">
            Chọn gói phù hợp với nhu cầu của bạn. Tất cả gói đều dùng thử miễn phí 7 ngày.
          </p>
        </div>

        {error && (
          <div className="mb-8 p-4 text-error bg-error/10 rounded-lg text-center" role="alert">
            {error}
          </div>
        )}

        {/* Comparison Table */}
        <div className="table-container card overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th className="w-48">Tính năng</th>
                {plans.map(plan => (
                  <th key={plan.id} className={`text-center ${plan.id === 'pro' ? 'bg-brand/5' : ''}`}>
                    <div className={`inline-flex items-center gap-2 mb-2 ${plan.id === 'pro' ? 'text-brand' : 'text-text-muted'}`}>
                      <span className="font-semibold text-lg display">{plan.displayName}</span>
                      {plan.id === 'pro' && (
                        <span className="badge badge-brand">Phổ biến</span>
                      )}
                    </div>
                    <div className="text-3xl font-bold text-text display mb-1">
                      {plan.priceMonthly === 0 ? 'Miễn phí' : new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', minimumFractionDigits: 0 }).format(plan.priceMonthly)}
                    </div>
                    <div className="text-sm text-text-muted">
                      {plan.priceYearly && plan.priceYearly > 0 ? (
                        <>Hoặc {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', minimumFractionDigits: 0 }).format(plan.priceYearly)}/năm (tiết kiệm {Math.round((1 - plan.priceYearly / (plan.priceMonthly * 12)) * 100)}%)</>
                      ) : (
                        '/tháng'
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allFeatures.map((feature) => (
                <tr key={feature.key}>
                  <td className="font-medium text-text">{feature.label}</td>
                  {plans.map(plan => (
                    <td key={plan.id} className="text-center">
                      {hasFeature(plan, feature.key) ? (
                        <svg className="w-5 h-5 mx-auto text-success" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 mx-auto text-border" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 max-w-5xl mx-auto">
          {plans.map(plan => (
            <div key={plan.id} className={`card p-6 ${plan.id === 'pro' ? 'ring-2 ring-brand' : ''} ${user?.planId === plan.id ? 'ring-2 ring-success' : ''}`}>
              {user?.planId === plan.id ? (
                <button className="btn-outline w-full py-3" disabled>
                  Gói hiện tại
                </button>
              ) : (
                <button
                  onClick={() => handleCreateQr(plan.id)}
                  className={`w-full py-3 ${plan.id === 'pro' ? 'btn-primary' : 'btn-outline'}`}
                >
                  {plan.priceMonthly === 0 ? 'Bắt đầu miễn phí' : 'Nâng cấp'}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* QR Code Modal */}
        {qrData && selectedPlan && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={handleCloseQr}>
            <div className="card w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={handleCloseQr}
                className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 text-text-muted focus-ring"
                aria-label="Đóng"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <h2 className="text-xl font-bold text-text mb-2 text-center display">Thanh toán nâng cấp</h2>
              <p className="text-text-muted text-center mb-4">
                Quét mã QR để thanh toán <strong>{formatPrice(plans.find(p => p.id === selectedPlan)?.priceMonthly ?? 0, 'monthly')}</strong>
              </p>

              {qrError && (
                <div className="mb-4 p-3 text-sm text-error bg-error/10 rounded-lg" role="alert">
                  {qrError}
                </div>
              )}

              <div className="text-center mb-4">
                <img
                  src={qrData.qr_url}
                  alt={`QR code thanh toán ${qrData.reference}`}
                  className="w-48 h-48 mx-auto border border-border rounded-lg bg-white p-2"
                />
              </div>

              <div className="space-y-2 text-sm text-text-muted mb-4">
                <p><strong>Nội dung:</strong> {qrData.reference}</p>
                <p><strong>Số tiền:</strong> {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(qrData.amount)}</p>
                <p><strong>Hết hạn:</strong> {new Date(qrData.expires_at).toLocaleString('vi-VN')}</p>
              </div>

              <div className="p-3 bg-bg rounded-lg text-sm text-text-muted">
                <p className="font-medium mb-1">Hướng dẫn:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Mở app ngân hàng / SePay / MoMo / ZaloPay</li>
                  <li>Quét mã QR hoặc nhập số tiền & nội dung</li>
                  <li>Thanh toán, gói sẽ kích hoạt tự động trong 1-2 phút</li>
                </ol>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => navigator.clipboard.writeText(qrData.reference)}
                  className="btn-outline flex-1 text-sm focus-ring"
                >
                  Copy nội dung
                </button>
                <button
                  onClick={handleCloseQr}
                  className="btn-primary flex-1 focus-ring"
                >
                  Đã thanh toán
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}